import React, { useState, useEffect } from 'react';
import { Plus, X, Phone, Mail, Calendar, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../../components/DataTable/DataTable';
import Breadcrumb from '../../../components/Common/Breadcrumb';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import salesPipelineService from '../../../services/salesPipelineService';
import customerMasterService from '../../../services/customerMasterService';
import serviceMasterService from '../../../services/serviceMasterService';
import userService from '../../../services/userService';
import { toast } from 'react-toastify';
import '../../../components/ConfirmationModal/ConfirmationModal.css';

const formatCurrency = (amount, currency = 'INR') => {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount || 0);
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

const STATUS_BADGE = { open: 'bg-info', won: 'bg-success', lost: 'bg-danger' };

const ACTIVITY_TYPES = ['call', 'email', 'meeting', 'demo', 'POC'];

const OpportunityList = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [stages, setStages] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [activities, setActivities] = useState([]);
  const [activityForm, setActivityForm] = useState({ type: 'call', summary: '', nextStep: '', nextFollowUpDate: '' });
  const [filters, setFilters] = useState({ stageId: '', ownerUserId: '', status: '', fromDate: '', toDate: '', page: 1, search: '' });

  useEffect(() => {
    loadLookups();
  }, []);

  useEffect(() => {
    loadData();
  }, [filters.stageId, filters.ownerUserId, filters.status, filters.fromDate, filters.toDate, filters.page, filters.search]);

  const loadLookups = async () => {
    try {
      const [stg, cust, svc, usr] = await Promise.all([
        salesPipelineService.getStages(),
        customerMasterService.getAllCustomers({ limit: 500 }),
        serviceMasterService.getAllServices({ limit: 500 }),
        userService.getAllUsers({ limit: 200 })
      ]);
      if (stg?.success) setStages(stg.data || []);
      if (cust?.success) setCustomers(cust.data || []);
      if (svc?.success) setServices(svc.data || []);
      if (usr?.data) setUsers(usr.data); else if (Array.isArray(usr)) setUsers(usr);
    } catch (e) {
      console.error(e);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const params = { page: filters.page, limit: 20 };
      if (filters.stageId) params.stageId = filters.stageId;
      if (filters.ownerUserId) params.ownerUserId = filters.ownerUserId;
      if (filters.status) params.status = filters.status;
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;
      if (filters.search) params.search = filters.search;
      const res = await salesPipelineService.getOpportunities(params);
      if (res?.success) {
        setData((res.data || []).map(item => ({ ...item, id: item._id })));
      }
    } catch (err) {
      toast.error('Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (opp) => {
    setSelectedOpp(opp);
    setShowDetailDrawer(true);
    try {
      const [oppRes, actRes] = await Promise.all([
        salesPipelineService.getOpportunityById(opp._id),
        salesPipelineService.getActivities(opp._id)
      ]);
      if (oppRes?.success) setSelectedOpp(oppRes.data);
      if (actRes?.success) setActivities(actRes.data || []);
    } catch (e) {
      toast.error('Failed to load details');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      customerId: '',
      productId: '',
      expectedValue: '',
      currency: 'INR',
      stageId: stages[0]?._id || '',
      probabilityOverride: '',
      expectedCloseDate: '',
      ownerUserId: '',
      nextFollowUpDate: '',
      source: '',
      status: 'open',
      notes: ''
    });
    setEditingItem(null);
  };

  const [formData, setFormData] = useState({
    name: '', customerId: '', productId: '', expectedValue: '', currency: 'INR',
    stageId: '', probabilityOverride: '', expectedCloseDate: '', ownerUserId: '', nextFollowUpDate: '', source: '', status: 'open', notes: ''
  });

  useEffect(() => {
    if (stages.length && !formData.stageId) setFormData(f => ({ ...f, stageId: stages[0]._id }));
  }, [stages]);

  const handleAdd = () => {
    resetForm();
    setFormData(f => ({ ...f, stageId: stages[0]?._id || '' }));
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      customerId: item.customerId?._id || item.customerId || '',
      productId: item.productId?._id || item.productId || '',
      expectedValue: item.expectedValue ?? '',
      currency: item.currency || 'INR',
      stageId: item.stageId?._id || item.stageId || '',
      probabilityOverride: item.probabilityOverride ?? '',
      expectedCloseDate: item.expectedCloseDate ? new Date(item.expectedCloseDate).toISOString().split('T')[0] : '',
      ownerUserId: item.ownerUserId?._id || item.ownerUserId || '',
      nextFollowUpDate: item.nextFollowUpDate ? new Date(item.nextFollowUpDate).toISOString().split('T')[0] : '',
      source: item.source || '',
      status: item.status || 'open',
      notes: item.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = (item) => {
    setDeleteItem(item);
    setShowDeleteModal(true);
  };

  const handleSave = async () => {
    if (!formData.name?.trim()) { toast.error('Name is required'); return; }
    if (!formData.expectedValue || formData.expectedValue < 0) { toast.error('Expected value is required'); return; }
    if (!formData.stageId) { toast.error('Stage is required'); return; }
    try {
      const payload = { ...formData, expectedValue: Number(formData.expectedValue) };
      if (payload.probabilityOverride === '') delete payload.probabilityOverride; else payload.probabilityOverride = Number(payload.probabilityOverride);
      if (editingItem) {
        const res = await salesPipelineService.updateOpportunity(editingItem._id, payload);
        if (res?.success) { toast.success('Updated'); setShowModal(false); loadData(); } else toast.error(res?.message || 'Failed');
      } else {
        const res = await salesPipelineService.createOpportunity(payload);
        if (res?.success) { toast.success('Created'); setShowModal(false); loadData(); } else toast.error(res?.message || 'Failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save');
    }
  };

  const handleAddActivity = async () => {
    if (!selectedOpp) return;
    try {
      const res = await salesPipelineService.addActivity(selectedOpp._id, {
        type: activityForm.type,
        summary: activityForm.summary,
        nextStep: activityForm.nextStep,
        nextFollowUpDate: activityForm.nextFollowUpDate || undefined
      });
      if (res?.success) {
        setActivities(a => [res.data, ...a]);
        setActivityForm({ type: 'call', summary: '', nextStep: '', nextFollowUpDate: '' });
        if (res.data?.nextFollowUpDate) setSelectedOpp(o => ({ ...o, nextFollowUpDate: res.data.nextFollowUpDate }));
        loadData();
      }
    } catch (e) {
      toast.error('Failed to add activity');
    }
  };

  const handleStageChange = async (opp, newStageId) => {
    try {
      const res = await salesPipelineService.updateOpportunity(opp._id, { stageId: newStageId });
      if (res?.success) {
        setSelectedOpp(o => ({ ...o, stageId: res.data?.stageId || newStageId }));
        loadData();
        toast.success('Stage updated');
      }
    } catch (e) {
      toast.error('Failed to update stage');
    }
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    try {
      const res = await salesPipelineService.deleteOpportunity(deleteItem._id);
      if (res?.success) { toast.success('Deleted'); setShowDeleteModal(false); setDeleteItem(null); loadData(); } else toast.error(res?.message || 'Failed');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const getProbability = (row) => {
    if (row.probabilityOverride != null) return row.probabilityOverride;
    return row.stageId?.defaultProbability ?? 0;
  };

  const getWeighted = (row) => {
    const p = getProbability(row) / 100;
    return (row.expectedValue || 0) * p;
  };

  const columns = [
    { accessorKey: 'name', header: 'Name', cell: ({ getValue, row }) => <span className="fw-medium" style={{ cursor: 'pointer', color: 'var(--primary)' }} onClick={() => loadDetail(row.original)}>{getValue()}</span> },
    { accessorKey: 'customerId', header: 'Customer', cell: ({ row }) => row.original.customerId?.customerName || '—' },
    { accessorKey: 'productId', header: 'Product/Service', cell: ({ row }) => row.original.productId?.serviceDescription || row.original.productId?.serviceCode || '—' },
    { accessorKey: 'expectedValue', header: 'Value', cell: ({ getValue, row }) => formatCurrency(getValue(), row.original.currency) },
    { accessorKey: 'stageId', header: 'Stage', cell: ({ row }) => row.original.stageId?.name || '—' },
    { accessorKey: 'probability', header: 'Probability', cell: ({ row }) => `${getProbability(row.original)}%` },
    { accessorKey: 'weighted', header: 'Weighted', cell: ({ row }) => formatCurrency(getWeighted(row.original), row.original.currency) },
    { accessorKey: 'ownerUserId', header: 'Owner', cell: ({ row }) => row.original.ownerUserId?.name || '—' },
    { accessorKey: 'nextFollowUpDate', header: 'Next Follow-up', cell: ({ getValue }) => formatDate(getValue()) },
    { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <span className={`badge ${STATUS_BADGE[getValue()] || 'bg-secondary'}`}>{getValue()}</span> }
  ];

  const breadcrumbItems = [
    { label: 'Sales', path: '/sales', onClick: () => navigate('/sales') },
    { label: 'Transactions', path: '/sales?tab=transactions', onClick: () => navigate('/sales?tab=transactions') },
    { label: 'Opportunities', path: '/sales/opportunities', isCurrent: true }
  ];

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section"><Breadcrumb items={breadcrumbItems} /></div>
      <div className="page-header">
        <div className="page-title">
          <h1>Opportunities</h1>
          <p>Manage sales pipeline and opportunities</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={handleAdd}><Plus size={16} /> Add Opportunity</button>
        </div>
      </div>

      <div className="d-flex gap-2 flex-wrap mb-3">
        <input type="text" className="form-control" style={{ maxWidth: 200 }} placeholder="Search name" value={filters.search} onChange={(e) => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))} />
        <select className="form-select" style={{ width: 'auto' }} value={filters.stageId} onChange={(e) => setFilters(f => ({ ...f, stageId: e.target.value, page: 1 }))}>
          <option value="">All stages</option>
          {stages.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
        </select>
        <select className="form-select" style={{ width: 'auto' }} value={filters.ownerUserId} onChange={(e) => setFilters(f => ({ ...f, ownerUserId: e.target.value, page: 1 }))}>
          <option value="">All owners</option>
          {users.map(u => <option key={u._id} value={u._id}>{u.name || u.userEmail}</option>)}
        </select>
        <select className="form-select" style={{ width: 'auto' }} value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}>
          <option value="">All status</option>
          <option value="open">Open</option>
          <option value="won">Won</option>
          <option value="lost">Lost</option>
        </select>
        <input type="date" className="form-control" style={{ width: 'auto' }} value={filters.fromDate} onChange={(e) => setFilters(f => ({ ...f, fromDate: e.target.value, page: 1 }))} placeholder="From" />
        <input type="date" className="form-control" style={{ width: 'auto' }} value={filters.toDate} onChange={(e) => setFilters(f => ({ ...f, toDate: e.target.value, page: 1 }))} placeholder="To" />
      </div>

      <DataTable data={data} columns={columns} loading={loading} onEdit={handleEdit} onDelete={handleDelete} noCard pageSize={20} />

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="confirmation-modal-overlay" onClick={(e) => e.target === e.currentTarget && (setShowModal(false), resetForm())}>
          <div className="confirmation-modal" style={{ maxWidth: 520, maxHeight: '90vh', overflow: 'auto' }}>
            <div className="confirmation-modal-header">
              <h3 className="confirmation-modal-title mb-0">{editingItem ? 'Edit Opportunity' : 'Add Opportunity'}</h3>
              <button className="confirmation-modal-close" onClick={() => (setShowModal(false), resetForm())}><X size={20} /></button>
            </div>
            <div className="confirmation-modal-content" style={{ padding: '1.5rem' }}>
              <div className="row g-2">
                <div className="col-12"><label className="form-label">Name *</label><input type="text" className="form-control" value={formData.name} onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))} /></div>
                <div className="col-md-6"><label className="form-label">Customer</label><select className="form-select" value={formData.customerId} onChange={(e) => setFormData(f => ({ ...f, customerId: e.target.value }))}><option value="">Select</option>{customers.map(c => <option key={c._id} value={c._id}>{c.customerName}</option>)}</select></div>
                <div className="col-md-6"><label className="form-label">Product/Service</label><select className="form-select" value={formData.productId} onChange={(e) => setFormData(f => ({ ...f, productId: e.target.value }))}><option value="">Select</option>{services.map(s => <option key={s._id} value={s._id}>{s.serviceDescription || s.serviceCode}</option>)}</select></div>
                <div className="col-md-6"><label className="form-label">Expected Value *</label><input type="number" min="0" className="form-control" value={formData.expectedValue} onChange={(e) => setFormData(f => ({ ...f, expectedValue: e.target.value }))} /></div>
                <div className="col-md-6"><label className="form-label">Stage *</label><select className="form-select" value={formData.stageId} onChange={(e) => setFormData(f => ({ ...f, stageId: e.target.value }))}>{stages.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}</select></div>
                <div className="col-md-6"><label className="form-label">Probability Override %</label><input type="number" min="0" max="100" className="form-control" value={formData.probabilityOverride} onChange={(e) => setFormData(f => ({ ...f, probabilityOverride: e.target.value }))} placeholder="Optional" /></div>
                <div className="col-md-6"><label className="form-label">Expected Close Date</label><input type="date" className="form-control" value={formData.expectedCloseDate} onChange={(e) => setFormData(f => ({ ...f, expectedCloseDate: e.target.value }))} /></div>
                <div className="col-md-6"><label className="form-label">Owner</label><select className="form-select" value={formData.ownerUserId} onChange={(e) => setFormData(f => ({ ...f, ownerUserId: e.target.value }))}><option value="">Select</option>{users.map(u => <option key={u._id} value={u._id}>{u.name || u.userEmail}</option>)}</select></div>
                <div className="col-md-6"><label className="form-label">Next Follow-up</label><input type="date" className="form-control" value={formData.nextFollowUpDate} onChange={(e) => setFormData(f => ({ ...f, nextFollowUpDate: e.target.value }))} /></div>
                <div className="col-12"><label className="form-label">Notes</label><textarea className="form-control" rows={2} value={formData.notes} onChange={(e) => setFormData(f => ({ ...f, notes: e.target.value }))} /></div>
                {editingItem && <div className="col-md-6"><label className="form-label">Status</label><select className="form-select" value={formData.status} onChange={(e) => setFormData(f => ({ ...f, status: e.target.value }))}><option value="open">Open</option><option value="won">Won</option><option value="lost">Lost</option></select></div>}
              </div>
            </div>
            <div className="confirmation-modal-footer" style={{ padding: '1rem 1.5rem' }}>
              <button className="confirmation-modal-cancel" onClick={() => (setShowModal(false), resetForm())}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}>{editingItem ? 'Update' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Drawer */}
      {showDetailDrawer && selectedOpp && (
        <div className="confirmation-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowDetailDrawer(false)}>
          <div className="confirmation-modal" style={{ position: 'fixed', right: 0, top: 0, bottom: 0, maxWidth: 420, margin: 0, borderRadius: 0 }} onClick={e => e.stopPropagation()}>
            <div className="confirmation-modal-header d-flex justify-content-between align-items-center">
              <h3 className="confirmation-modal-title mb-0">{selectedOpp.name}</h3>
              <button className="confirmation-modal-close" onClick={() => setShowDetailDrawer(false)}><X size={20} /></button>
            </div>
            <div className="confirmation-modal-content" style={{ padding: '1rem', overflow: 'auto' }}>
              <div className="mb-3">
                <label className="form-label small text-muted">Stage</label>
                <select className="form-select form-select-sm" value={selectedOpp.stageId?._id || selectedOpp.stageId} onChange={(e) => handleStageChange(selectedOpp, e.target.value)}>
                  {stages.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                </select>
              </div>
              <p className="mb-1"><strong>Value:</strong> {formatCurrency(selectedOpp.expectedValue, selectedOpp.currency)}</p>
              <p className="mb-1"><strong>Customer:</strong> {selectedOpp.customerId?.customerName || '—'}</p>
              <p className="mb-2"><strong>Status:</strong> <span className={`badge ${STATUS_BADGE[selectedOpp.status] || 'bg-secondary'}`}>{selectedOpp.status}</span></p>

              <hr />
              <h6>Add Activity</h6>
              <div className="mb-2">
                <select className="form-select form-select-sm mb-2" value={activityForm.type} onChange={(e) => setActivityForm(f => ({ ...f, type: e.target.value }))}>
                  {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <input type="text" className="form-control form-control-sm mb-2" placeholder="Summary" value={activityForm.summary} onChange={(e) => setActivityForm(f => ({ ...f, summary: e.target.value }))} />
                <input type="text" className="form-control form-control-sm mb-2" placeholder="Next step" value={activityForm.nextStep} onChange={(e) => setActivityForm(f => ({ ...f, nextStep: e.target.value }))} />
                <input type="date" className="form-control form-control-sm mb-2" placeholder="Next follow-up" value={activityForm.nextFollowUpDate} onChange={(e) => setActivityForm(f => ({ ...f, nextFollowUpDate: e.target.value }))} />
                <button className="btn btn-sm btn-primary" onClick={handleAddActivity}>Add</button>
              </div>
              <hr />
              <h6>Activity Timeline</h6>
              <div style={{ maxHeight: 200, overflow: 'auto' }}>
                {activities.length === 0 ? <p className="text-muted small">No activities yet</p> : activities.map(a => (
                  <div key={a._id} className="border rounded p-2 mb-2 small">
                    <span className="badge bg-light text-dark me-2">{a.type}</span> {formatDate(a.activityDate)}
                    {a.summary && <p className="mb-0 mt-1">{a.summary}</p>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal isOpen={showDeleteModal} onClose={() => (setShowDeleteModal(false), setDeleteItem(null))} onConfirm={confirmDelete} title="Delete Opportunity" message={`Delete "${deleteItem?.name}"?`} confirmText="Delete" type="danger" />
    </div>
  );
};

export default OpportunityList;
